<template>
  <div class="calendar calendar-scroll-target horizontal" :class="darkMode ? 'dark' : 'light'">
    <div class="times">
      <div class="time f8" v-for="time in calendarHeaderTimes" :key="time">{{ time }}</div>
    </div>
    <div class="stationsandbookings" @mousemove.self="setCoordinates" @mouseleave="getOuttaHereHover">
      <div class="station-row" :class="{ '-maintenance': getMaintenanceMode(stationId), 'lightBackground': alternateRowColour(index) }" v-for="(stationId, index) in stationIds" :id="index" :key="stationId">
        <BookingCard
          v-for="booking in getStationBookings(stationId)"
          :key="booking.id"
          :booking="booking"
          :stationId="stationId"
          :twentyFourHourTime="twentyFourHourTime"
          :calendarDate="calendarDate"
          :isNetworkAvailable="isNetworkAvailable"
          :isOrganizationEducation="isOrganizationEducation"
          @mouseenter.native="getOuttaHereHover"
          @click.native="showBookingPopup(stationId, booking.id, darkMode)"
        />
        <ClosingCard
          v-for="closing in getStationClosings(stationId)"
          :key="closing.id"
          :closing="closing"
          :stationId="stationId"
          :twentyFourHourTime="twentyFourHourTime"
          :calendarDate="calendarDate"

          :isNetworkAvailable="isNetworkAvailable"
          @mouseenter.native="getOuttaHereHover"
          @click.native="showClosingPopup(stationId, closing.id, darkMode)"
        />
      </div>
      <CalendarHover @click.native="openWalkin" :time="hoverTime" :coordinates="hoverCoordinates" />
    </div>

    <div class="gradient"></div>

    <UseVuex v-if="showNowLine" v-slot="{ state }">
      <NowLine
       :currentTime="state.timer.currentTime"
       :darkMode="darkMode" />
    </UseVuex>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import moment from 'moment-timezone';
import CalendarHover from './CalendarHover';
import NowLine from './NowLine';
import UseVuex from '@/core/shared/components/utils/UseVuex';
import BookingCard from './BookingCard';
import ClosingCard from './ClosingCard';

@Component({
  props: {
    calendarHeaderTimes: {
      type: Array,
      required: true,
    },
    showNowLine: {
      type: Boolean,
      required: true,
    },
    experiences: {
      type: Array,
      required: true,
    },
    stations: {
      type: Array,
      required: true,
    },
    bookings: {
      type: Array,
      required: true,
    },
    closings: {
      type: Array,
      required: true,
    },
    calendarDate: {
      type: String,
      required: true,
    },
    twentyFourHourTime: {
      type: Boolean,
      required: true,
    },
    isNetworkAvailable: {
      type: Boolean,
      required: true,
    },
    isOrganizationEducation: {
      type: Boolean,
      required: true,
    },
    darkMode: {
      type: Boolean,
      required: false,
    },
  },
  components: {
    CalendarHover,
    NowLine,
    UseVuex,
    BookingCard,
    ClosingCard,
  },
  watch: {
    experiences: {
      handler: 'createStationIdArray',
    },
    stations: {
      handler: 'createStationIdArray',
      immediate: true,
      deep: true,
    },
  },
})
export default class Calendar extends Vue {
  hoverCoordinates = { x: -1000, y: -1000 };
  hoverTime = null;
  hoverStationId = null;

  stationIds = [];

  createStationIdArray () {
    this.stationIds = this.experiences.reduce((stationIds, experience) => {
      const stationsInExperience = this.stations.filter(station => station.experience.id === experience.id).sort((a, b) => a.order - b.order);
      return [...stationIds, ...stationsInExperience.map(({ id }) => id)];
    }, []);
  }

  openWalkin () {
    let startTime;

    if (!this.twentyFourHourTime) {
      let hours = parseInt(this.hoverTime.split(':')[0]);
      const minutes = parseInt(this.hoverTime.split(':')[1].slice(0, 2));
      const AMPM = this.hoverTime.split(' ')[1].toLowerCase();

      if (AMPM === 'pm' && hours !== 12) {
        hours += 12;
      }
      if (AMPM === 'am' && hours === 12) {
        hours = 0;
      }

      startTime = moment(this.calendarDate).hours(hours).minutes(minutes).seconds(0).format();
    } else {
      const hours = parseInt(this.hoverTime.split(':')[0]);
      const minutes = parseInt(this.hoverTime.split(':')[1].slice(0, 2));
      startTime = moment(this.calendarDate).hours(hours).minutes(minutes).seconds(0).format();
    }

    const hoverStation = this.stations.find(({ id }) => id === this.hoverStationId);

    this.$router.push({
      name: 'walkin',
      params: {
        preSelected: {
          startTime,
          bookingStationTimes: [
            {
              station: {
                id: hoverStation.id,
              },
              experience: {
                id: hoverStation.experience.id,
              },
            },
          ],
        },
        darkMode: this.darkMode,
      },
    });
  }

  setCoordinates (e) {
    const { left, top } = e.srcElement.getBoundingClientRect();
    const width = e.srcElement.clientWidth / this.calendarHeaderTimes.length;
    const height = e.srcElement.clientHeight / this.stationIds.length;

    const x = e.clientX - left;
    const quantizedX = Math.floor(x / width) * width;

    const y = e.clientY - top;
    const quantizedY = Math.floor(y / height) * height;

    this.hoverTime = this.calendarHeaderTimes[Math.floor(quantizedX / width)];
    this.hoverStationId = this.stationIds[Math.floor(quantizedY / height)];

    this.hoverCoordinates = { x: quantizedX, y: quantizedY };
  }

  getOuttaHereHover () {
    this.hoverCoordinates = { x: -1000, y: -1000 };
  }

  getStationBookings (stationId) {
    return this.bookings.filter(booking => {
      return ~booking.bookingStationTimes.findIndex(bookingStationTime => bookingStationTime.station.id === stationId);
    });
  }

  getStationClosings (stationId) {
    return this.closings.filter(closing => {
      return closing.station?.id === stationId;
    });
  }

  getMaintenanceMode (stationId) {
    const station = this.stations.find(({ id }) => stationId === id);
    return (station) ? station.maintenanceModeEnabled : false;
  }

  showBookingPopup (stationId, bookingId, darkMode) {
    if (bookingId && !bookingId.includes('loading')) {
      this.$router.push({
        name: 'booking-detail',
        params: { stationId, bookingId, darkMode },
      });
    }
  }

  showClosingPopup (stationId, closingId, darkMode) {
    if (closingId && !closingId.includes('loading')) {
      this.$router.push({
        name: 'closing-detail',
        params: { stationId, closingId, darkMode },
      });
    }
  }

  calculateIndex (index, color) {
    const indexColor = index % 2 === 0 ? 'light' : 'dark';
    return indexColor === color;
  }

  alternateRowColour (index) {
    if (this.darkMode) {
      if (index % 2 === 0) {
        return true;
      }
    }
  }
}
</script>

<style scoped lang="postcss">
@import 'core/shared/styles';

.calendar {
  position: relative;
  overflow: scroll;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  position: relative;

  & .times {
    display: flex;
    background-color: var(--colorWhite);
    width: 480rem;

    & .time {
      flex: 0 0 10rem;
      margin: 0;
      padding: var(--spacingSm);
      text-align: center;
      transform: translateX(-50%);
    }
  }

  & .stationsandbookings {
    overflow: hidden;
    min-height: 3rem;
    width: 480rem;

    & .station-row {
      pointer-events: none;
      position: relative;
      height: 13rem;
      background-color: var(--colorCasper);
      background-image: url('./calendar-grid-horizontal.svg');

      &.-maintenance {
        background-color: #e9ecec;
      }
    }

    & .booking-card {
      position: absolute;
      pointer-events: auto;
      z-index: 1;
    }

    & .closing-card {
      position: absolute;
      pointer-events: auto;
      z-index: 1;
    }
  }

  & .gradient {
    position: absolute;
    top: 0;
    left: 0;
    background: linear-gradient(90deg, var(--colorWhite) 0%, #ffffff00 100%);
    width: 5rem;
    height: 4.3rem;
  }
}

.calendar.dark .times .time {
  background-color: var(--darkModeColorDarkBackground)  !important;
  color: var(--darkModeColorText) !important;
}

.calendar.dark .times {
  background-color: var(--darkModeColorDarkBackground) !important;
}

.calendar.dark .stationsandbookings .station-row {
  background-color: var(--darkModeColorDarkBackground);
}

.calendar.dark .stationsandbookings .station-row .booking-card {
  background-color: var(--darkModeColorBackground);
  color: var(--darkModeColorText) !important;
}

.calendar.dark .stationsandbookings .lightBackground .booking-card {
  background-color: var(--darkModeColorDarkBackground);
  color: var(--darkModeColorText) !important;
}

.dark::-webkit-scrollbar {
  width: 3px;
  height: 14px;
}

.dark::-webkit-scrollbar-track {
  background: var(--darkModeColorDarkBackground);
}

.dark::-webkit-scrollbar-thumb {
  background: var(--darkModeColorBackground);
  border-radius: 10px;
  border: 2px solid var(--darkModeColorBackground);
}

.dark::-webkit-scrollbar-thumb:hover {
background: var(--darkModeColorBackground);
}

.lightBackground {
  background-color: var(--darkModeColorBackground) !important;
}

</style>
